/**
 * 登录页用的线路状态：启动时探一遍，给界面一个「● 主线路 · 46ms」可看，
 * 也让「切换线路」有据可点。网页版没有线路概念，返回空。
 */

import { useCallback, useEffect, useState } from 'react'

import { isDesktop } from './desktop'
import { autoSelect } from './lineSelect'
import { currentServer, pinServer, pinnedServer, serverList, setCurrentServer } from './servers'
import type { ProbeResult, ServerInfo } from './servers'

export interface LineState {
  enabled: boolean
  list: ServerInfo[]
  current: ServerInfo | null
  probes: ProbeResult[]
  pinned: string | null
  /** 正在探测 */
  busy: boolean
  /** 全部线路都不通 */
  allDown: boolean
  /** 手动选一条；传 null 回到自动 */
  pick: (id: string | null) => Promise<void>
  /** 重新探测 */
  refresh: () => Promise<void>
}

export function useLine(): LineState {
  const enabled = isDesktop()
  const [list, setList] = useState<ServerInfo[]>(() => (enabled ? serverList() : []))
  const [current, setCurrent] = useState<ServerInfo | null>(() => (enabled ? currentServer() : null))
  const [probes, setProbes] = useState<ProbeResult[]>([])
  const [pinned, setPinned] = useState<string | null>(() => (enabled ? pinnedServer() : null))
  const [busy, setBusy] = useState(false)
  const [allDown, setAllDown] = useState(false)

  const refresh = useCallback(async () => {
    if (!enabled) return
    setBusy(true)
    try {
      const r = await autoSelect()
      setList(serverList())
      setProbes(r.probes)
      setCurrent(r.server ?? currentServer())
      setAllDown(!r.server)
    } finally {
      setBusy(false)
    }
  }, [enabled])

  const pick = useCallback(
    async (id: string | null) => {
      pinServer(id)
      setPinned(id)
      if (id) setCurrentServer(id)
      await refresh()
    },
    [refresh],
  )

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { enabled, list, current, probes, pinned, busy, allDown, pick, refresh }
}
